import React, {useEffect, useState} from "react";
import styles from "./EditAdminContractInfo.module.css";
import Button from "../../../common/button/Button";
import {GetAdminContractResponseData, UpdateContractRequest} from "../../../../types/admin";
import {adminContractAPI, updateContractAPI} from "../../../../services/admin";
import TextCard from "../../../common/textCard/TextCard";
import {useNavigate, useParams} from "react-router-dom";
import {ContractStatus} from "../../../../types/contract";
import AdminLayout from "../../layout/AdminLayout";
import {useAlert} from "../../../common/error/AlertContext";

// 컴포넌트 내부용 타입 (Date 객체 사용)
interface InternalFormData {
    contractId: number,
    period: number,
    startedAt: Date,
    expireAt: Date,
    workerAmount: number,
    unitCost: number,
    payday: number
}

// 어드민 계약 정보 수정
const EditAdminContractInfo: React.FC = () => {
    const navigate = useNavigate();
    const {showAlert} = useAlert();
    const {id} = useParams<{ id: string }>();
    const [data, setData] = useState<GetAdminContractResponseData | null>(null);
    const [loading, setLoading] = useState(true);

    // 내부적으로는 Date 객체로 관리
    const [formData, setFormData] = useState<InternalFormData>({
        contractId: 0,
        period: 0,
        startedAt: new Date(),
        expireAt: new Date(),
        workerAmount: 0,
        unitCost: 0,
        payday: 0
    });

    // Date 객체를 YYYY-MM-DD 문자열로 변환
    //날짜 데이터 형식 변환, 없으면 '-', 있으면 시간 데이터 제외
    const formatDateToString = (date: Date | string): string => {
        if (!date) return '';
        const dateObj = date instanceof Date ? date : new Date(date);

        if (isNaN(dateObj.getTime())) {
            return '-';
        }
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // API 전송용 데이터 변환
    const getApiFormData = (): UpdateContractRequest => ({
        contractId: formData.contractId,
        period: formData.period,
        startedAt: formatDateToString(formData.startedAt),
        expireAt: formatDateToString(formData.expireAt),
        workerAmount: formData.workerAmount,
        unitCost: formData.unitCost,
        payday: formData.payday
    });

    //계약 종료일 계산
    //계약일 + 계약 기간 - 1일
    //ex) 시작일: 01.01 / 계약 기간: 3달 > 계약 종료일: 03.31
    const calculateEndDate = (startedAt: Date, months: number): Date => {
        let year = startedAt.getFullYear();
        let month = startedAt.getMonth() + months + 1;

        if (month > 12) {
            year += Math.floor((month - 1) / 12);
            month = month % 12;
            if (month === 0) month = 12;
        }

        const endDate = new Date(year, month - 1, startedAt.getDate() - 1);
        const lastDayOfMonth = new Date(year, month - 1, 0).getDate();
        if (endDate.getDate() > lastDayOfMonth) {
            endDate.setDate(lastDayOfMonth);
        }
        return endDate;
    };

    const fetchData = async () => {
        if (!id) return;
        try {
            setLoading(true);
            // 계약 정보 조회 API
            const response = await adminContractAPI(parseInt(id));
            setData(response);

            if (response.contractStatus === ContractStatus.예정) {
                setFormData({
                    contractId: response.contractId,
                    period: response.period,
                    startedAt: new Date(response.startedAt),
                    expireAt: new Date(response.expireAt),
                    workerAmount: response.workerAmount,
                    unitCost: response.unitCost,
                    payday: parseInt(response.payday)
                });
            }
        } catch (error) {
            console.error('Error fetching contract:', error);
            navigate('/admin/clients/contract');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [id]);

    useEffect(() => {
        if (data?.contractStatus === ContractStatus.예정) {
            // expireAt 계산
            const expireAt = calculateEndDate(formData.startedAt, formData.period);
            // payday를 startedAt의 일자로 설정
            const newPayday = new Date(formData.startedAt).getDate();

            setFormData(prev => ({
                ...prev,
                expireAt,
                payday: newPayday
            }));
        }
    }, [formData.startedAt, formData.period]);

    const handleSubmit = async () => {
        try {
            // Date 객체를 문자열로 변환하여 전송
            const requestData = getApiFormData();

            // 계약 정보 수정 API
            await updateContractAPI(requestData);
            showAlert('수정되었습니다.', "info");
            // 조회 페이지로 이동
            navigate(`/admin/clients/contract/${id}`);
        } catch (error) {
            console.error('Error updating contract:', error);
            showAlert('수정 중 오류가 발생했습니다.', "error");
        }
    };

    // 날짜 데이터 형식 변환 (화면 표시용)
    const formatDate = (date: Date | string | undefined): string => {
        if (!date) return '';
        const dateObj = date instanceof Date ? date : new Date(date);

        if (isNaN(dateObj.getTime())) {
            return '-';
        }
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const formatExpireDate = (date: Date | undefined) => {
        if (!date) return '-';
        const dateObj = date instanceof Date ? date : new Date(date);
        return dateObj.toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        })
    };

    // 클라이언트 정보
    const getCompanyInfo = () => {
        return [
            {
                label: "회사명",
                value: data?.companyName || '-',
                type: "static" as const
            },
            {
                label: "사업자등록번호",
                value: data?.businessNumber || '-',
                type: "static" as const
            }
        ];
    };

    // 날짜 정보
    const getDateInfo = () => {
        // 예정 상태인 계약만 수정 가능
        const isEditable = data?.contractStatus === ContractStatus.예정;

        if (isEditable) {
            return [
                // 계약 기간 선택
                {
                    label: "계약 기간",
                    value: `${formData.period}개월` || '-',
                    type: "text" as const,
                    onChange: (value: string) => {
                        const newPeriod = parseInt(value.replace(/[^0-9]/g, '')) || 0;
                        setFormData(prev => ({
                            ...prev,
                            period: newPeriod
                        }));
                    }
                },
                // 계약 시작일 선택
                {
                    label: "계약일",
                    value: formatDate(formData.startedAt) || '-',
                    type: "date" as const,
                    onChange: (value: string) => {
                        const newStartAt = new Date(value);
                        setFormData(prev => ({
                            ...prev,
                            startedAt: newStartAt
                        }));
                    }
                },
                // 계약 만료일 자동 기입
                {
                    label: "만료일",
                    value: formatExpireDate(formData.expireAt) || '-',
                    type: "static" as const
                }
            ];
        } else {
            return [
                // 조회
                {
                    label: "계약 기간",
                    value: `${data?.period}개월` || '-',
                    type: "static" as const
                },
                {
                    label: "계약일",
                    value: formatDate(data?.startedAt) || '-',
                    type: "static" as const
                },
                {
                    label: "만료일",
                    value: formatDate(data?.expireAt) || '-',
                    type: "static" as const
                }
            ];
        }
    };

    const getContractInfo = () => {
        // 예정 상태인 계약만 수정 가능
        const isEditable = data?.contractStatus === ContractStatus.예정;

        if (isEditable) {
            return [
                // 작업자 수 선택
                {
                    label: "작업자 수",
                    value: `${formData.workerAmount}명` || '-',
                    type: "text" as const,
                    onChange: (value: string) => {
                        const newAmount = parseInt(value.replace(/[^0-9]/g, '')) || 0;
                        setFormData(prev => ({
                            ...prev,
                            workerAmount: newAmount
                        }));
                    }
                },
                //작업자 단가 수정
                {
                    label: "작업자 단가",
                    value: `${formData.unitCost.toLocaleString()}원` || '-',
                    type: "text" as const,
                    onChange: (value: string) => {
                        const newUnitCost = parseInt(value.replace(/[^0-9]/g, '')) || 0;
                        setFormData(prev => ({
                            ...prev,
                            unitCost: newUnitCost
                        }));
                    }
                },
                //계약 상태는 고정
                {
                    label: "계약 상태",
                    value: ContractStatus.예정,
                    type: "static" as const
                }
            ];
        } else {
            return [
                // 조회
                {
                    label: "작업자 수",
                    value: `${data?.workerAmount}명` || '-',
                    type: "static" as const
                },
                {
                    label: "작업자 단가",
                    value: `${data?.unitCost.toLocaleString()}원` || '-',
                    type: "static" as const
                },
                {
                    label: "계약 상태",
                    value: data?.contractStatus || '-',
                    type: "static" as const
                }
            ];
        }
    };

    if (loading || !data) {
        return <div className={styles.loading}>Loading...</div>;
    }

    return (
        <AdminLayout>
            <div className={styles.container}>
                <div className={styles.licenseSection}>
                    {data?.contractStatus === ContractStatus.예정 && (
                        <div className={styles.buttonContainer}>
                            {/*저장 버튼*/}
                            <Button
                                onClick={handleSubmit}
                                variant="primary"
                                className={styles.button}
                            >
                                저장
                            </Button>
                            {/*취소 버튼*/}
                            <Button
                                onClick={() => navigate(`/admin/clients/contract/${id}`)}
                                variant="secondary"
                                className={styles.button}
                            >
                                취소
                            </Button>
                        </div>
                    )}
                    <div className={styles.cardWrapper}>
                        <div className={styles.progressContainer}>
                            {/*계약 진행률 원형 그래프*/}
                            <CircularProgress progress={data.progress}/>
                        </div>
                        <div className={styles.infoContainer}>
                            {/*계약 정보*/}
                            <TextCard items={getCompanyInfo()} className={styles.fullWidth}/>
                            <TextCard items={getDateInfo()} className={styles.fullWidth}/>
                            <TextCard items={getContractInfo()} className={styles.fullWidth}/>
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
};

// 원형 그래프
const CircularProgress: React.FC<{ progress: number }> = ({progress}) => {
    const radius = 80;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (progress / 100) * circumference;

    return (
        <div className={styles.progressWrapper}>
            <svg className={styles.progressSvg} viewBox="0 0 192 192">
                <circle
                    cx="96"
                    cy="96"
                    r={radius}
                    stroke="#E0E0E0"
                    strokeWidth="10"
                    fill="none"
                />
                <circle
                    cx="96"
                    cy="96"
                    r={radius}
                    stroke="#002387"
                    strokeWidth="10"
                    fill="none"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    strokeLinecap="round"
                />
            </svg>
            <div className={styles.progressText}>
                <span>{progress}%</span>
            </div>
        </div>
    );
};

export default EditAdminContractInfo;